import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Users, Calendar, User, Eye } from "lucide-react";

function deriveSchedule(c) {
  if (Array.isArray(c?.schedule) && c.schedule.length) return c.schedule;
  if (Array.isArray(c?.days) && c?.time) {
    const [start, end] = String(c.time).split("-").map(s => s.trim());
    return c.days.map(d => ({ day: d, start: start || "", end: end || "" }));
  }
  return [];
}

export default function ClassDetail() {
  const { id } = useParams();
  const [klass, setKlass] = useState(null);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/classes"),
          api.get("/students"),
        ]);
        setKlass(c.data.find(x => x.id === id) || null);
        setStudents(s.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [id]);

  const roster = useMemo(
    () => students.filter(s => s.class_id === id).sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [students, id]
  );

  if (!klass) {
    return <div data-testid="class-detail-loading" className="text-muted-foreground">Carregando...</div>;
  }

  const sched = deriveSchedule(klass);
  const active = roster.filter(s => s.status === "ativo").length;
  const pendente = roster.filter(s => s.status === "pendente").length;

  return (
    <div data-testid="admin-class-detail-page">
      <div className="mb-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/turmas"><ArrowLeft className="size-4 mr-2" /> Voltar</Link>
        </Button>
      </div>

      <PageHeader
        eyebrow={klass.category}
        title={klass.name}
        subtitle={klass.description || "Detalhes da turma e lista de alunos matriculados."}
        mascot={BRAND.MANGUITA_COACH}
      />

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <div className="vv-card p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Users className="size-4 text-[hsl(var(--vv-lime))]" /> Total
          </div>
          <div className="text-3xl font-black mt-1">{roster.length}</div>
          <div className="text-xs text-muted-foreground">
            {active} ativo(s){pendente ? ` • ${pendente} pendente(s)` : ""}
          </div>
        </div>
        <div className="vv-card p-4 sm:col-span-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
            <Calendar className="size-4 text-[hsl(var(--vv-yellow))]" /> Horários
          </div>
          {sched.length ? (
            <div className="flex flex-wrap gap-2">
              {sched.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-muted/40 rounded px-3 py-1.5">
                  <span className="vv-chip vv-chip-blue">{s.day}</span>
                  <span className="font-bold text-[hsl(var(--vv-yellow))]">{s.start}</span>
                  <span className="text-muted-foreground">às</span>
                  <span className="font-bold text-[hsl(var(--vv-yellow))]">{s.end}</span>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-muted-foreground">Sem horários definidos.</div>}
        </div>
      </div>

      <div className="vv-card overflow-hidden">
        <div className="p-4 border-b border-border font-black uppercase tracking-wider text-sm flex items-center justify-between">
          <span>Alunos matriculados</span>
          <span className="text-xs text-muted-foreground font-normal normal-case">{roster.length} no total</span>
        </div>
        {roster.length === 0 ? (
          <EmptyState title="Nenhum aluno nesta turma" description="Vincule alunos editando o perfil de cada um." mascot={BRAND.MANGUITA} />
        ) : (
          <div className="divide-y divide-border">
            {roster.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-3" data-testid={`class-roster-${s.id}`}>
                {s.photo_url ? (
                  <img src={s.photo_url} alt="" className="size-11 rounded-full object-cover border-2 border-[hsl(var(--vv-lime))/0.4]" />
                ) : (
                  <div className="size-11 rounded-full bg-muted flex items-center justify-center">
                    <User className="size-5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {s.position || "Sem posição"} • {s.category}
                  </div>
                </div>
                <span className={`vv-chip whitespace-nowrap ${
                  s.status === "ativo" ? "vv-chip-lime" :
                  s.status === "pendente" ? "vv-chip-yellow" : "vv-chip-muted"
                }`}>{s.status}</span>
                <Button asChild size="icon" variant="ghost" title="Abrir ficha do aluno">
                  <Link to={`/admin/alunos/${s.id}`}><Eye className="size-4" /></Link>
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
